"use client";

import * as React from "react";
import { ChevronLeft, ChevronRight } from '@/shared/ui/icons';
import { DayPicker } from "react-day-picker";

import { uiCursor } from "./cursor";
import { cn } from "./utils";

const filterGeneratedProps = (props: any) => {
  const clean: any = {};
  for (const key in props) {
    if (!key.startsWith("_fg")) {
      clean[key] = props[key];
    }
  }
  return clean;
};

const SafeIcon = ({ icon: Icon, ...props }: { icon: any, [key: string]: any }) => {
  const clean = filterGeneratedProps(props);
  return <Icon {...clean} />;
};

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  ...props
}: React.ComponentProps<typeof DayPicker>) {
  const cleanProps = filterGeneratedProps(props);

  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      navLayout="around"
      className={cn("p-[0.75rem]", className)}
      classNames={{
        months: "flex flex-col gap-[0.5rem]",
        month:
          "grid w-[17.5rem] grid-cols-[2.25rem_minmax(0,1fr)_2.25rem] grid-rows-[2.25rem_auto] items-center gap-y-[0.75rem]",
        month_caption: "col-start-2 row-start-1 flex h-9 items-center justify-center",
        caption_label:
          "inline-flex items-center justify-center whitespace-nowrap px-10 text-center text-[13px] font-[var(--font-weight-medium)] text-calendar-text",
        nav: "hidden",
        button_previous:
          `col-start-1 row-start-1 inline-flex h-9 w-9 items-center justify-center self-center bg-transparent p-0 text-calendar-text-muted transition-colors hover:text-calendar-text disabled:opacity-30 ${uiCursor.interactive}`,
        button_next:
          `col-start-3 row-start-1 inline-flex h-9 w-9 items-center justify-center self-center bg-transparent p-0 text-calendar-text-muted transition-colors hover:text-calendar-text disabled:opacity-30 ${uiCursor.interactive}`,
        chevron: "h-4 w-4 fill-current",
        month_grid: "col-span-3 row-start-2 w-full table-fixed border-collapse",
        weekdays: "w-full",
        weekday:
          "h-8 align-middle text-center text-calendar-text-muted font-[var(--font-weight-medium)] text-[11px]",
        weeks: "w-full",
        week: "w-full",
        day: "h-10 w-10 p-0 text-center align-middle",
        day_button:
          `mx-auto inline-flex h-9 w-9 items-center justify-center rounded-full border-0 bg-transparent p-0 font-[var(--font-weight-regular)] text-[13px] text-calendar-text outline-none transition-all hover:bg-calendar-hover-bg focus-visible:ring-0 aria-selected:opacity-100 ${uiCursor.interactive}`,
        selected:
          "bg-calendar-selected-bg text-calendar-selected-text hover:bg-calendar-selected-bg hover:text-calendar-selected-text focus:bg-calendar-selected-bg focus:text-calendar-selected-text rounded-full",
        // Highlight today as a guide for the user — brand-coloured
        // text with a matching ring so it stands out from the rest
        // of the grid even when it isn't the selected day. The
        // `selected` style still wins when today happens to be
        // selected (Tailwind class order — `selected` is applied
        // by react-day-picker after `today`, so its bg + text
        // override).
        today:
          "text-brand font-[var(--font-weight-semibold)] [&>button]:ring-1 [&>button]:ring-brand [&>button]:ring-inset",
        outside: "text-calendar-text-muted opacity-50",
        disabled: "text-calendar-text-muted opacity-30",
        hidden: "invisible",
        range_start:
          "bg-calendar-selected-bg text-calendar-selected-text rounded-full",
        range_middle:
          "bg-calendar-range-bg text-calendar-range-text rounded-none",
        range_end:
          "bg-calendar-selected-bg text-calendar-selected-text rounded-full",
        ...classNames,
      }}
      components={{
        Chevron: ({ className, orientation, ...props }) => (
          <SafeIcon
            icon={orientation === "right" ? ChevronRight : ChevronLeft}
            className={cn("size-4", className)}
            {...props}
          />
        ),
      }}
      {...cleanProps}
    />
  );
}

export { Calendar };
